import { IconLock } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import ContestReferralCard from './ContestReferralCard';
import ContestBonusTasks from './ContestBonusTasks';
import { possibleTaskKeys, rewardSum } from '../../utils/contestTasks';
import { CARD, useNumFmt } from './contestUi';

// Left column of the contest page — how to earn contest points (they are NOT credits):
//   • own payments: the owner earns a % of everything they pay while the contest runs (automatic)
//   • a friend's payments: via the referral link (automatic, capped per payment) — joining alone earns nothing
//   • bonus tasks: share (daily) / follow / review / video review — claimed here, awarded by the contest team
// Earning only works while the contest is live; before / after it only a note is shown.
// Contest points never touch the credit wallet.

function WayCard({ value, tone, title, desc, wide }) {
    return (
        <div className={`flex flex-col gap-1.5 rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900 ${wide ? 'col-span-2' : ''}`}>
            <div className={`font-baloo text-[26px] font-bold leading-[1.1] ${tone}`}>{value}</div>
            <div className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</div>
            <div className="text-[13px] text-zinc-500 dark:text-zinc-400">{desc}</div>
        </div>
    );
}

export default function ContestEarnCard({ data, phase: pagePhase, onChanged }) {
    const { t } = useTranslation();
    const fmt = useNumFmt();

    if (!data?.contest) return null;

    // The page-level phase (same source as the hero) wins, so the earning options vanish the moment the
    // contest is no longer live — even if this /me response is a moment behind.
    const phase = pagePhase && pagePhase !== 'live' ? pagePhase : data.phase;

    // Contest not running (upcoming / pending / completed): no earning options — only why.
    if (phase !== 'live') {
        return (
            <section className={`${CARD} flex items-start gap-3 p-[22px]`}>
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"><IconLock size={20} /></span>
                <div className="flex min-w-0 flex-col gap-0.5">
                    <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t(phase === 'upcoming' ? 'contest.earn.waysTitle' : 'contest.earn.lockedTitle')}</div>
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t(phase === 'upcoming' ? 'contest.earn.notStarted' : phase === 'pending' ? 'contest.earn.closedPending' : 'contest.earn.closedCompleted')}
                    </div>
                </div>
            </section>
        );
    }

    const { rules } = data;
    // Total of the bonus-task rewards this owner can still earn over the whole contest (switched-off tasks and installs already
    // paid in an earlier contest left out; all three app reviews count — one per app).
    const bonusTotal = rewardSum(data, possibleTaskKeys(data));

    // The ways to earn, in the order of the points breakdown. A way whose rate is 0 is switched off.
    const ways = [];
    if (rules.pointsPerCredit > 0) {
        ways.push({ key: 'credit', value: `${fmt(1)} = ${fmt(rules.pointsPerCredit)}`, tone: 'text-zinc-900 dark:text-zinc-100', title: t('contest.earn.fromUsage'), desc: t('contest.earn.wayCreditDesc', { n: fmt(rules.pointsPerCredit) }) });
    }
    if (rules.ownPaymentPercent > 0) {
        ways.push({ key: 'own', value: `${fmt(rules.ownPaymentPercent)}%`, tone: 'text-primary', title: t('contest.earn.fromOwnPayment'), desc: t('contest.earn.wayOwnDesc', { n: fmt(rules.ownPaymentPercent) }) });
    }
    if (rules.referralPaymentPercent > 0) {
        ways.push({ key: 'friend', value: `${fmt(rules.referralPaymentPercent)}%`, tone: 'text-doc-blue-700 dark:text-doc-blue-300', title: t('contest.earn.wayFriendTitle'), desc: t('contest.earn.wayFriendDesc', { n: fmt(rules.referralPaymentPercent), max: fmt(rules.referralMaxPoints ?? 75), laterPercent: fmt(rules.referralLaterPercent ?? 50), laterMax: fmt(rules.referralLaterMaxPoints ?? 35) }) });
    }
    if (bonusTotal > 0) {
        ways.push({ key: 'bonus', value: `+${fmt(bonusTotal)}`, tone: 'text-emerald-700 dark:text-emerald-400', title: t('contest.earn.claimTitle'), desc: t('contest.earn.wayBonusDesc') });
    }

    return (
        <div className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-col gap-3">
                <div className="font-baloo text-[21px] font-bold text-zinc-900 dark:text-zinc-100">{t('contest.earn.waysTitle')}</div>
                <div className="grid grid-cols-2 gap-2.5">
                    {ways.map((w, i) => (
                        <WayCard key={w.key} value={w.value} tone={w.tone} title={w.title} desc={w.desc} wide={ways.length % 2 === 1 && i === ways.length - 1} />
                    ))}
                </div>
            </div>

            <ContestBonusTasks data={data} onChanged={onChanged} />
            <ContestReferralCard data={data} />
        </div>
    );
}
